/**
 * requirements — per-page rules about WHICH imports a page can operate on.
 *
 * A page passes ONE predicate to `ImportSelectionBar`, which renders it in two
 * places: greyed out on a finder row (`short`), and above the finder as the full
 * sentence about the current shared selection (`long`).
 *
 * TWO INVARIANTS, both load-bearing:
 *
 * 1. ONE predicate, two renderings. A row and the notice above it can never
 *    disagree about the same import, because there is nothing to keep in sync.
 * 2. Rows are DISABLED, never hidden. Hiding is what makes rows vanish
 *    unexplained, and it is also what made the finder's page size and pager
 *    total disagree with what was on screen. Rendering everything and disabling
 *    what cannot be used keeps both numbers honest by construction.
 *
 * The accepted cost of (2) is that a page can fill with unpickable rows. If that
 * ever bites, the lever is ORDERING (`ready` first), NOT a server-side `status`
 * filter — filtering would hide rows again and re-break both counts.
 *
 * Rationale and alternatives considered: Decision 1 of
 * `.agent/Tasks/2026-08-11-import-picker-search-context/README.md`.
 */

import type { ImportSummary } from "@nu-assist/contracts";

/**
 * The fields a usability rule may read.
 *
 * Structural on purpose: both a full `ImportSummary` (a finder row) and a
 * `SelectedImport` (the stored projection) satisfy it, so one predicate serves
 * both surfaces without a conversion at either call site.
 *
 * `status`, `tags` and `import_format` — nothing else, so a rule cannot start
 * depending on another field by accident. `source` was a member until the
 * BCT-source exclusion was dropped, and stays withheld even though that
 * exclusion came back: it returned keyed on `tags`, not on `source`.
 *
 * Only `bctUnusable` reads a lineage field today (`tags`, mirroring the
 * server's `can_feed_bct = "bct" not in tagset`). `aiReviewUnusable` read
 * `import_format` until 2026-08-25, mirroring a server 409 that no longer
 * exists — see that function for the reversal.
 *
 * `import_format` stays a member even with no current reader: it is the
 * immutable-provenance discriminator, and the alternative a future rule would
 * otherwise reach for is the `ai` TAG, which is also worn by legacy in-place
 * reviews whose import is an ORIGINAL. Keeping it available is what makes the
 * right field the easy one.
 *
 * All optional fields are optional because they are optional on the wire.
 */
export interface ImportUsability {
  status: ImportSummary["status"];
  tags?: ImportSummary["tags"];
  import_format?: ImportSummary["import_format"];
}

/** Why an import cannot be used, at the two lengths the UI needs. */
export interface UnusableImport {
  /** Terse, for a cramped finder row. */
  short: string;
  /** Full sentence naming the user's next action, for the notice above it. */
  long: string;
}

/**
 * Why Backchannel Tree cannot run on this import, or `null` when it can.
 *
 * A `failed` or still-`parsing` import genuinely has no candidates to map yet,
 * and a BCT-DERIVED import (`bct` tag — the child `bct_worker` creates) can
 * never seed another tree: "You can not let them choose a bct map to make an
 * other map off of" (Jared, 2026-08-12; supersedes his earlier "no reason why
 * I couldn't do it again", which is about re-running BCT on the ORIGINAL
 * import — still allowed, the parent is deliberately never `bct`-tagged).
 *
 * The finder never shows BCT children on the BCT page (`excludeTag="bct"`
 * drops them server-side), so the `bct` branch here exists for the paths that
 * do NOT go through the finder: the app-wide selection carried over from AI
 * Review — where picking a BCT import is legitimate — and a hand-written
 * `?importId=` deep link.
 */
export function bctUnusable(item: ImportUsability): UnusableImport | null {
  // Server-side sibling: `derive_action_flags` in the API's imports domain
  // computes `can_feed_bct = "bct" not in tagset` — the same child-vs-parent
  // discriminator. If this rule flips again, change both (plus BctPage's
  // `excludeTag="bct"`).
  //
  // Fail-open on a MISSING `tags` field is deliberate: the wire type marks
  // `tags` optional, and this branch is only the safety net behind the
  // server-side `exclude_tag` filter — "server sent no tags" must not brick
  // every legitimate selection.
  if ((item.tags ?? []).includes("bct")) {
    return {
      short: "Created by Company Map",
      long: "This people list was created by a Company Map run, so it can't be used as the source of another map. Pick the original people list instead.",
    };
  }
  if (item.status === "ready") return null;
  if (item.status === "failed") {
    return {
      short: "Couldn't be read",
      long: "We couldn't read this people list, so it has no candidates to map. Pick another people list.",
    };
  }
  return {
    short: "Still processing",
    long: "This people list is still being processed. Pick another people list, or wait until it's ready.",
  };
}

/**
 * Why AI Review cannot run on this import, or `null` when it can.
 *
 * One rule: the list has to be readable. Unlike `bctUnusable` above, there is
 * no lineage condition — a Company Map cannot seed another map because its
 * child is a different SHAPE (companies, not contacts) and a second pass has
 * nothing to read; a review file is a contact list with extra columns.
 *
 * It used to refuse one thing more — a derived review file, "you review the
 * original, never a review of a review" (epic decision D5). **Reversed
 * 2026-08-25** at Jared's request (Trello #711 cl.5): scoring a list and then
 * scoring the narrowed result on new criteria is the flow, not a mistake. The
 * server's matching 409 went with it, as did `can_feed_ai`'s tag condition, so
 * the three still agree — which was always the point of keeping them in step.
 *
 * This predicate stays an AFFORDANCE only. The enforcement is server-side, and
 * a stale tab or a script reaches the endpoint without consulting it.
 */
export function aiReviewUnusable(item: ImportUsability): UnusableImport | null {
  if (item.status === "ready") return null;
  if (item.status === "failed") {
    return {
      short: "Couldn't be read",
      long: "We couldn't read this people list, so it has no candidates to score. Pick another people list.",
    };
  }
  return {
    short: "Still processing",
    long: "This people list is still being processed. Pick another people list, or wait until it's ready.",
  };
}

/**
 * Why Combine & Filter cannot use this list as a source, or `null` when it
 * can.
 *
 * Only readiness: any TYPE of people list can be combined, a combined list
 * included (epic 2026-09-22, "Sources" row). A list still being parsed has
 * no rows yet, and `POST /combine/preview` answers 409 for it — before this
 * rule (bug hunt 2026-09-23, F12) the picker let it be selected and the
 * page showed that 409 as a broken preview.
 */
export function combineUnusable(item: ImportUsability): UnusableImport | null {
  if (item.status === "ready") return null;
  if (item.status === "failed") {
    return {
      short: "Couldn't be read",
      long: "We couldn't read this people list, so it has no contacts to combine. Pick another people list.",
    };
  }
  return {
    short: "Still processing",
    long: "This people list is still being processed. Pick another people list, or wait until it's ready.",
  };
}
